import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

const DEFAULT_CELL_SX = {
  py: 0.3,
  px: 0.65,
  fontSize: "0.65rem",
  borderBottom: "1px solid #f1f5f9",
};
const DEFAULT_HEAD_SX = {
  ...DEFAULT_CELL_SX,
  fontWeight: 700,
  fontSize: "0.58rem",
  color: "#64748b",
  bgcolor: "#f8fafc",
  borderBottom: "1px solid #e8edf3",
};
const DEFAULT_SCROLL_SX = {
  overflowY: "auto",
  overflowX: "hidden",
  minHeight: 0,
  flex: 1,
  "&::-webkit-scrollbar": { width: 5 },
  "&::-webkit-scrollbar-thumb": { backgroundColor: "#cbd5e1", borderRadius: 8 },
};

const EmptyState = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flex: 1,
      width: "100%",
      height: "100%",
      minHeight: 80,
      borderRadius: "8px",
      bgcolor: "#f8fafc",
      border: "1px dashed #e2e8f0",
      px: 1,
    }}
  >
    <Typography sx={{ fontSize: "0.7rem", color: "#64748b", textAlign: "center" }}>
      No data available
    </Typography>
  </Box>
);

/**
 * Shared dense table shell used by Medicine Inventory Status, Pathology
 * Inventory Status and Available Doctors — replaces what used to be three
 * near-identical TableContainer/Table blocks. Callers own all domain logic
 * (status chip colors, row actions, cell content) via `column.render`.
 *
 * columns: [{ key, header, align, render(row) }]
 * rows: array of domain row objects, each ideally carrying an `id`
 */
const StatusTable = ({
  columns = [],
  rows = [],
  headerBg,
  cellSx = DEFAULT_CELL_SX,
  headCellSx = DEFAULT_HEAD_SX,
  scrollSx = DEFAULT_SCROLL_SX,
  getRowKey = (row, index) => row.id ?? index,
  variant = "table",
  renderPrimary,
  renderSecondary,
  renderStatus,
}) => {
  if (!rows.length) {
    return <EmptyState />;
  }

  if (variant === "list") {
    return (
      <Box sx={{ ...scrollSx, maxHeight: "100%", minWidth: 0, display: "flex", flexDirection: "column" }}>
        {rows.map((row, index) => (
          <Box
            key={getRowKey(row, index)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              py: 0.6,
              borderBottom: index < rows.length - 1 ? "1px solid #f1f5f9" : "none",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: "#0f172a",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  lineHeight: 1.3,
                }}
              >
                {renderPrimary ? renderPrimary(row) : row.name}
              </Typography>
              {renderSecondary ? (
                <Typography sx={{ fontSize: "0.62rem", color: "#94a3b8", fontWeight: 500, lineHeight: 1.3 }}>
                  {renderSecondary(row)}
                </Typography>
              ) : null}
            </Box>
            {renderStatus ? <Box sx={{ flexShrink: 0 }}>{renderStatus(row)}</Box> : null}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <TableContainer sx={{ ...scrollSx, maxHeight: "100%", minWidth: 0 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                align={col.align || "left"}
                sx={headerBg ? { ...headCellSx, bgcolor: headerBg } : headCellSx}
              >
                {col.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={getRowKey(row, index)}>
              {columns.map((col) => (
                <TableCell key={col.key} align={col.align || "left"} sx={cellSx}>
                  {col.render ? col.render(row) : row[col.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default React.memo(StatusTable);
